/**
 * The brand name shown in the header, and the customer's override of it.
 *
 * <h2>An override, not a replacement</h2>
 * The product already has a brand per market — integralDelta.ai for US/International, Dhanabhyas
 * for India — resolved from the customer's own profile. This adds a personal override ON TOP of
 * that: absent one, the market's brand is what shows, so clearing the override restores the right
 * name for the right customer rather than pinning everyone to the US one.
 *
 * <h2>Where it does and does not apply</h2>
 * The header and the browser tab. The chart watermark keeps the market brand deliberately: a
 * watermark is a provenance mark on an image people export and share, and a personal nickname
 * there would misattribute the chart.
 *
 * <h2>Storage can fail, and that is not an error</h2>
 * localStorage throws in private modes and under some enterprise policies. Every function here
 * degrades to "no override" rather than propagating, because a header that cannot be renamed is a
 * missing nicety and a header that throws is a blank application.
 */

/** The key, exactly as specified. */
export const BRAND_STORAGE_KEY = 'custom_app_brand_name';

/**
 * Fired when the override changes in THIS tab.
 *
 * <p>The `storage` event only reaches other tabs — the one that made the change never hears about
 * it — so a same-tab listener is what makes the header update without a reload. Both are used.
 */
export const BRAND_CHANGED_EVENT = 'astrocharts:brand-changed';

/**
 * The longest name accepted.
 *
 * <p>The header is one line beside the symbol and the user menu. A name past this does not wrap, it
 * pushes the menu off the bar — so it is refused at the input rather than discovered on screen.
 */
export const MAX_BRAND_LENGTH = 40;

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_) {
    return null;                       // access itself throws under some policies
  }
}

/**
 * ⚠ BRANDS A STORED OVERRIDE MUST NO LONGER WIN WITH.
 *
 * <h2>Why a rename alone was not enough</h2>
 *
 * The short-bar toggle WRITES the product name into this key — `writeBrandOverride(
 * SHORT_BAR_BRAND)` — so the stored value is usually a side effect of a layout preference rather
 * than a deliberate rename. Renaming the constant therefore changes what the toggle writes NEXT
 * time and nothing about the value already sitting in every existing admin's browser: the header
 * would keep reading `integralDelta` after a rebrand, for everyone, permanently, with no visible
 * cause. Found by reasoning about the write path rather than on screen, because a developer who
 * has never toggled the bar sees the new name immediately and would not reproduce it.
 *
 * <p>⚠ IT DISCARDS, IT DOES NOT REWRITE. A migration that stored the new brand would turn a
 * leftover into a real override and pin that admin to this name through the next rename too.
 * Treating it as absent lets the market brand resolve, which is the whole point of the fallback.
 *
 * <p>The cost is stated: an admin who DELIBERATELY typed one of these as their own nickname loses
 * it once. Against every admin being stuck on a retired brand, that is the cheaper failure, and
 * re-entering a name takes one dialog.
 */
export const SUPERSEDED_BRANDS = [
  'integralDelta',
  'integralDelta.ai',
];

/** The stored override, or null when there is none. */
export function readBrandOverride() {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(BRAND_STORAGE_KEY);
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    // A stored empty string is treated as no override. It cannot be written through this module,
    // but it can arrive from a hand-edited devtools entry, and honouring it would render " - SPY".
    if (!trimmed) return null;
    // Case-insensitive: the value can be hand-edited, and `INTEGRALDELTA` is the same leftover.
    if (SUPERSEDED_BRANDS.some(b => b.toLowerCase() === trimmed.toLowerCase())) return null;
    return trimmed.slice(0, MAX_BRAND_LENGTH);
  } catch (_) {
    return null;
  }
}

/**
 * Stores an override. Blank clears it, so "save an empty box" and "reset" agree.
 *
 * @returns {string|null} the override now in force
 */
export function writeBrandOverride(name) {
  const trimmed = typeof name === 'string' ? name.trim().slice(0, MAX_BRAND_LENGTH) : '';
  const s = storage();
  try {
    if (!trimmed) s?.removeItem(BRAND_STORAGE_KEY);
    else s?.setItem(BRAND_STORAGE_KEY, trimmed);
  } catch (_) {
    // Quota or a blocked store. The value below still takes effect for this session through the
    // event; only its persistence is lost, which is the lesser half of the feature.
  }
  notify(trimmed || null);
  return trimmed || null;
}

/** Removes the override, so the market's own brand shows again. */
export function clearBrandOverride() {
  return writeBrandOverride('');
}

function notify(value) {
  try {
    window.dispatchEvent(new CustomEvent(BRAND_CHANGED_EVENT, { detail: value }));
  } catch (_) { /* no window, or events unavailable: the caller still has the return value */ }
}

/**
 * The name to show: the override if there is one, else the market's own brand.
 *
 * @param {string} marketBrand what the customer's market is called
 */
export function resolveBrandName(marketBrand) {
  return readBrandOverride() || marketBrand || '';
}

/**
 * Subscribes to changes from either this tab or another one.
 *
 * @param {(value: string|null) => void} onChange
 * @returns {() => void} unsubscribe
 */
export function onBrandChange(onChange) {
  const local = e => onChange(e && 'detail' in e ? e.detail : readBrandOverride());
  // Another tab's write. Filtered by key, because `storage` fires for every key in the origin and
  // an unrelated write would otherwise re-render the header for nothing.
  const remote = (e) => {
    if (!e || e.key === null || e.key === BRAND_STORAGE_KEY) onChange(readBrandOverride());
  };
  window.addEventListener(BRAND_CHANGED_EVENT, local);
  window.addEventListener('storage', remote);
  return () => {
    window.removeEventListener(BRAND_CHANGED_EVENT, local);
    window.removeEventListener('storage', remote);
  };
}
